import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  TrendingDown, Plus, Trash2, RefreshCw, ExternalLink, Bell, BellOff,
  Target, Loader2, Info, Plane,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

/* Price Watch — fare-drop monitoring for flight routes.
   Deep links always work; live cached-fare checks activate when the backend
   has a Travelpayouts token (live_enabled from /price-watch/status). */

const inp = "w-full border border-zinc-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-sky-700";
const lbl = "block text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1";
const money = (n, c) => (n == null ? "—" : `${Number(n).toLocaleString("en-US")} ${c}`);
const ago = (iso) => {
  if (!iso) return null;
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins}m ago`;
  const h = Math.round(mins / 60);
  return h < 48 ? `${h}h ago` : `${Math.round(h / 24)}d ago`;
};

function WatchCard({ w, liveEnabled, onDelete, onToggle, onCheck, onTarget }) {
  const [checking, setChecking] = useState(false);
  const [editTarget, setEditTarget] = useState(false);
  const [tval, setTval] = useState(w.target_price || "");
  const hitTarget = w.target_price && w.last_price != null && w.last_price <= w.target_price;

  const check = async () => {
    setChecking(true);
    try { await onCheck(w.id); } finally { setChecking(false); }
  };
  const saveTarget = async () => {
    const v = parseFloat(tval);
    await onTarget(w.id, Number.isFinite(v) && v > 0 ? v : null);
    setEditTarget(false);
  };

  return (
    <div className={`bg-white border p-5 ${w.active ? "border-zinc-300" : "border-zinc-200 opacity-60"}`}
      data-testid={`watch-card-${w.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-heading font-900 text-xl flex items-center gap-2">
            <Plane className="w-5 h-5 text-sky-700" /> {w.origin} → {w.destination}
            {w.return_date && <span className="text-xs font-mono font-400 text-muted-foreground">round trip</span>}
          </div>
          <div className="text-xs text-muted-foreground font-mono mt-1">
            Depart {w.depart_date}{w.return_date ? ` · Return ${w.return_date}` : ""} · {w.currency}
          </div>
        </div>
        <div className="text-right">
          {w.last_price != null ? (
            <>
              <div className={`font-heading font-900 text-2xl ${hitTarget ? "text-emerald-600" : ""}`} data-testid={`watch-price-${w.id}`}>
                {money(w.last_price, w.currency)}
              </div>
              <div className="text-[11px] text-muted-foreground font-mono">
                last seen {ago(w.last_found_at)}{w.best_price != null && w.best_price < w.last_price ? ` · best ${money(w.best_price, w.currency)}` : ""}
              </div>
            </>
          ) : (
            <div className="text-sm text-muted-foreground font-mono">{liveEnabled ? "no fare data yet" : "deep-link mode"}</div>
          )}
        </div>
      </div>

      {hitTarget && (
        <div className="mt-3 bg-emerald-50 border border-emerald-300 text-emerald-800 text-sm px-3 py-2 flex items-center gap-2" data-testid={`watch-target-hit-${w.id}`}>
          <Target className="w-4 h-4" /> At or below your target of {money(w.target_price, w.currency)} — verify the fare before booking.
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mt-4">
        {(w.links || []).map((l) => (
          <a key={l.label} href={l.url} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-zinc-300 text-xs font-700 hover:bg-zinc-50"
            data-testid={`watch-link-${l.label.toLowerCase().replace(/\s/g, "-")}-${w.id}`}>
            {l.label} <ExternalLink className="w-3 h-3" />
          </a>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-4 pt-3 border-t border-zinc-100">
        {liveEnabled && (
          <button onClick={check} disabled={checking} data-testid={`watch-check-${w.id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-sky-700 text-white text-xs font-700 hover:bg-sky-600 disabled:opacity-50">
            {checking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Check now
          </button>
        )}
        {editTarget ? (
          <span className="inline-flex items-center gap-1.5">
            <input type="number" min="1" value={tval} onChange={(e) => setTval(e.target.value)}
              className="border border-zinc-300 px-2 py-1 text-xs w-24" placeholder={`Target ${w.currency}`}
              data-testid={`watch-target-input-${w.id}`} />
            <button onClick={saveTarget} className="px-2 py-1 bg-[#4285F4] text-white text-xs font-700" data-testid={`watch-target-save-${w.id}`}>Save</button>
            <button onClick={() => setEditTarget(false)} className="px-2 py-1 text-xs text-muted-foreground">Cancel</button>
          </span>
        ) : (
          <button onClick={() => { setTval(w.target_price || ""); setEditTarget(true); }} data-testid={`watch-target-edit-${w.id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-zinc-300 text-xs font-700 hover:bg-zinc-50">
            <Target className="w-3.5 h-3.5" /> {w.target_price ? `Target: ${money(w.target_price, w.currency)}` : "Set target price"}
          </button>
        )}
        <button onClick={() => onToggle(w.id, !w.active)} data-testid={`watch-toggle-${w.id}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-zinc-300 text-xs font-700 hover:bg-zinc-50">
          {w.active ? <><Bell className="w-3.5 h-3.5 text-emerald-600" /> Watching</> : <><BellOff className="w-3.5 h-3.5" /> Paused</>}
        </button>
        <button onClick={() => onDelete(w.id)} data-testid={`watch-delete-${w.id}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-rose-300 text-rose-700 text-xs font-700 hover:bg-rose-50 ml-auto">
          <Trash2 className="w-3.5 h-3.5" /> Remove
        </button>
      </div>

      {(w.history || []).length > 1 && (
        <details className="mt-3">
          <summary className="text-xs font-mono text-muted-foreground cursor-pointer">Price history ({w.history.length} checks)</summary>
          <div className="mt-2 space-y-1">
            {w.history.slice(-10).reverse().map((h, i) => (
              <div key={i} className="flex justify-between text-xs font-mono text-zinc-600">
                <span>{h.checked_at?.slice(0, 16).replace("T", " ")}{h.airline ? ` · ${h.airline}` : ""}</span>
                <span className="font-700">{money(h.price, w.currency)}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

export default function PriceWatch() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({
    origin: (params.get("origin") || "").toUpperCase().slice(0, 3),
    destination: (params.get("dest") || "").toUpperCase().slice(0, 3),
    depart_date: params.get("date") || "", return_date: "", currency: "USD", target_price: "",
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/price-watch");
      setData(r.data);
    } catch { setData({ watches: [] }); }
  }, []);

  useEffect(() => {
    api.get("/price-watch/status").then((r) => setStatus(r.data)).catch(() => {});
    if (user) load();
  }, [user, load]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: k === "origin" || k === "destination" ? e.target.value.toUpperCase().slice(0, 3) : e.target.value }));

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/price-watch", {
        origin: form.origin, destination: form.destination, depart_date: form.depart_date,
        return_date: form.return_date || null, currency: form.currency,
        target_price: form.target_price ? parseFloat(form.target_price) : null,
      });
      toast.success("Watch created — we'll alert you on fare drops");
      setForm({ origin: "", destination: "", depart_date: "", return_date: "", currency: "USD", target_price: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create watch");
    } finally { setSaving(false); }
  };

  const onDelete = async (id) => {
    await api.delete(`/price-watch/${id}`);
    toast.success("Watch removed");
    load();
  };
  const onToggle = async (id, active) => { await api.put(`/price-watch/${id}`, { active }); load(); };
  const onTarget = async (id, target) => {
    await api.put(`/price-watch/${id}`, target ? { target_price: target } : { clear_target: true });
    load();
  };
  const onCheck = async (id) => {
    try {
      const r = await api.post(`/price-watch/${id}/check`);
      if (r.data.no_data) toast.info("No cached fares for this route/date yet — try the deep links");
      else if (r.data.result) toast.success(`Cheapest cached fare: ${r.data.result.price} ${r.data.watch.currency}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Check failed");
    }
  };

  const ready = form.origin.length === 3 && form.destination.length === 3 && /^\d{4}-\d{2}(-\d{2})?$/.test(form.depart_date);
  const liveEnabled = !!(data?.live_enabled ?? status?.live_enabled);

  return (
    <div className="min-h-screen bg-zinc-50">
      <div className="bg-[#34A853] text-white">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 py-12">
          <h1 className="font-heading font-900 text-4xl sm:text-5xl flex items-center gap-3">
            <TrendingDown className="w-10 h-10 text-[#FBBC05]" /> Price Watch
          </h1>
          <p className="text-green-50 mt-3 max-w-2xl text-base md:text-lg">
            Watch a route and get alerted when fares drop or hit your target price.
            One-click links to Google Flights, Kayak, Skyscanner and Aviasales.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-5 lg:px-8 py-10">
        {!liveEnabled && status && (
          <div className="mb-6 bg-amber-50 border border-amber-300 text-amber-800 text-sm px-4 py-3 flex items-start gap-2" data-testid="pw-deeplink-banner">
            <Info className="w-4 h-4 mt-0.5 shrink-0" />
            <span>Live fare monitoring is being set up — for now every watch gives you instant price-check links. Automatic drop alerts switch on once the fare data key is added.</span>
          </div>
        )}

        {!user ? (
          <div className="bg-white border border-zinc-300 p-8 text-center" data-testid="pw-login-prompt">
            <p className="text-sm text-zinc-700">Sign in to create price watches and get fare-drop alerts.</p>
            <Link to="/login" className="inline-block mt-3 px-5 py-2.5 bg-sky-700 text-white text-sm font-700 hover:bg-sky-600">Sign in</Link>
          </div>
        ) : (
          <>
            <div className="bg-white border border-zinc-300 p-5 mb-8" data-testid="pw-form">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div><label className={lbl}>From (IATA)</label>
                  <input value={form.origin} onChange={set("origin")} placeholder="JFK" className={`${inp} font-mono uppercase`} data-testid="pw-origin" /></div>
                <div><label className={lbl}>To (IATA)</label>
                  <input value={form.destination} onChange={set("destination")} placeholder="LHR" className={`${inp} font-mono uppercase`} data-testid="pw-dest" /></div>
                <div><label className={lbl}>Depart</label>
                  <input type="date" value={form.depart_date} onChange={set("depart_date")} className={inp} data-testid="pw-depart" /></div>
                <div><label className={lbl}>Return (optional)</label>
                  <input type="date" value={form.return_date} onChange={set("return_date")} className={inp} data-testid="pw-return" /></div>
                <div><label className={lbl}>Currency</label>
                  <select value={form.currency} onChange={set("currency")} className={inp} data-testid="pw-currency">
                    {(status?.currencies || ["USD", "EUR", "GBP"]).map((c) => <option key={c}>{c}</option>)}
                  </select></div>
                <div><label className={lbl}>Target price (optional)</label>
                  <input type="number" min="1" value={form.target_price} onChange={set("target_price")} placeholder="450" className={inp} data-testid="pw-target" /></div>
              </div>
              <button onClick={create} disabled={!ready || saving} data-testid="pw-create"
                className="mt-4 inline-flex items-center gap-1.5 px-5 py-2.5 bg-emerald-700 text-white text-sm font-700 hover:bg-emerald-600 disabled:opacity-40">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Watch this route
              </button>
            </div>

            {data === null ? (
              <div className="text-center py-10 text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
            ) : data.watches.length === 0 ? (
              <div className="text-center py-10 text-sm text-muted-foreground" data-testid="pw-empty">
                No watches yet — add a route above to start tracking fares.
              </div>
            ) : (
              <div className="space-y-4" data-testid="pw-list">
                {data.watches.map((w) => (
                  <WatchCard key={w.id} w={w} liveEnabled={liveEnabled}
                    onDelete={onDelete} onToggle={onToggle} onCheck={onCheck} onTarget={onTarget} />
                ))}
              </div>
            )}
          </>
        )}

        <p className="mt-10 text-[11px] text-muted-foreground font-mono">
          Prices are cached market data and may be out of date — always verify the fare on the booking site before purchase.
        </p>
      </div>
    </div>
  );
}
